import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      toast.success('Password reset! You can now log in.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-muted/30">
        <Card className="w-full max-w-md p-8 rounded-2xl shadow-sm text-center">
          <h1 className="text-2xl font-serif mb-4">Invalid Reset Link</h1>
          <p className="text-muted-foreground mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="rounded-full" data-testid="request-new-link-btn">Request a New Link</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-muted/30">
      <Card className="w-full max-w-md p-8 rounded-2xl shadow-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif mb-2" data-testid="reset-heading">Set New Password</h1>
          <p className="text-muted-foreground">Choose a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-form">
          <div>
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="reset-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="mt-2 rounded-xl border-2 border-muted-foreground/30"
              placeholder="At least 6 characters"
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              data-testid="reset-confirm-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-2 rounded-xl border-2 border-muted-foreground/30"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-btn"
            className="w-full rounded-full bg-primary hover:bg-primary/90"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground duration-300">
            ← Back to Login
          </Link>
        </div>
      </Card>
    </div>
  );
}
